import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { getToken, setToken } from '../utils/auth';
import {jwtDecode} from 'jwt-decode';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function Login() {
  const [telefono, setTelefono] = useState('');
  const [tieneContrasena, setTieneContrasena] = useState(null);
  const [contrasena, setContrasena] = useState('');
  const [nuevaContrasena, setNuevaContrasena] = useState('');
  const [confirmarContrasena, setConfirmarContrasena] = useState('');
  const [mostrarContrasena, setMostrarContrasena] = useState(false);
  const [loading, setLoading] = useState(false);
  const [esIncorrecto, setEsIncorrecto] = useState(false);

  const navigate = useNavigate();

  // Carga el teléfono guardado al montar
  useEffect(() => {
    const telefonoGuardado = localStorage.getItem('telefono');
    if (telefonoGuardado) {
      setTelefono(telefonoGuardado);
    }
  }, []);

  // Si ya hay un token válido, redirige; si está expirado o inválido, lo elimina
  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        const { exp } = jwtDecode(token);
        if (exp * 1000 > Date.now()) {
          navigate('/dashboard');
          return;
        }
      } catch (err) {
        console.error('❌ Token inválido:', err);
      }
      // Token expirado o inválido: lo borramos para evitar bucles de redirect
      localStorage.removeItem('token');
    }
  }, [navigate]);

  const comprobarTelefono = async () => {
    try {
      const res = await api.post('/auth/check-password', { telefono });
      setTieneContrasena(res.data.tieneContrasena);
    } catch (err) {
      console.error(err);
      toast.error('Teléfono no encontrado o error del servidor');
    }
  };

  const handleKeyDown = (e, accion) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      accion();
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { telefono, contrasena });
      setToken(res.data.token);
      toast.success('Inicio de sesión exitoso');
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
      toast.error('Credenciales incorrectas');
      setEsIncorrecto(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCrearContrasena = async () => {
    if (nuevaContrasena !== confirmarContrasena) {
      toast.warning('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/crear-contrasena', {
        telefono,
        contrasena: nuevaContrasena
      });
      const res = await api.post('/auth/login', {
        telefono,
        contrasena: nuevaContrasena
      });
      setToken(res.data.token);
      toast.success('Contraseña creada y sesión iniciada');
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
      toast.error('La contraseña no coincide o contiene menos de 4 caracteres.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login site-width">
      <ToastContainer position="bottom-right" autoClose={3000} hideProgressBar={false} />
      <div className="form-login">
        <h2>Inicia sesión</h2>

        {tieneContrasena === false ? (
          <p>¡Parece que es la primera vez que entras! Crea una contraseña segura para poder ver tus futuras citas.</p>
        ) : tieneContrasena === true ? (
          <p>Introduce tu teléfono para iniciar sesión y poder gestionar tus citas.</p>
        ) : null}

        <div className="campos-login">
          {tieneContrasena === null && (
            <div className="label-form">
              <label htmlFor="telefono">Número de teléfono:</label>
              <PhoneInput
                country="es"
                value={telefono}
                onChange={(value) => {
                  setTelefono(value);
                  localStorage.setItem('telefono', value);
                }}
                placeholder="Teléfono"
                inputProps={{
                  onKeyDown: (e) => handleKeyDown(e, comprobarTelefono)
                }}
              />
              <button className="boton-iniciar-sesion" onClick={comprobarTelefono}>
                Continuar
              </button>
            </div>
          )}

          {tieneContrasena === true && (
            <>
              <div className="label-form">
                <label htmlFor="contrasena">Contraseña:</label>
                <div className="contrasena-input">
                  <input
                    type={mostrarContrasena ? 'text' : 'password'}
                    placeholder="Contraseña"
                    value={contrasena}
                    onChange={(e) => setContrasena(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, handleLogin)}
                  />
                  <button type="button" tabIndex="-1" onClick={() => setMostrarContrasena(!mostrarContrasena)}>
                    {mostrarContrasena 
                      ? <img src="/images/ojo.svg" alt="Ocultar" /> 
                      : <img src="/images/ojo-cruzado.svg" alt="Mostrar" />}
                  </button>
                </div>
              </div>
              {esIncorrecto && (
                <div className="cuadro-recuperar">
                  <p>¿Olvidaste tu contraseña? Recupérala haciendo click <Link to='/recuperar-contrasena'>aquí</Link>.</p>
                </div>
              )}
              <button className="boton-iniciar-sesion" onClick={handleLogin} disabled={loading}>
                {loading ? 'Iniciando sesión...' : 'Iniciar sesión'}
              </button>
            </>
          )}

          {tieneContrasena === false && (
            <div className="crear-contrasena-form">
              <div className="label-form">
                <label htmlFor="nueva-contrasena">Nueva contraseña:</label>
                <div className="contrasena-input">
                  <input
                    type={mostrarContrasena ? 'text' : 'password'}
                    placeholder="Nueva contraseña"
                    value={nuevaContrasena}
                    onChange={(e) => setNuevaContrasena(e.target.value)}
                  />
                  <button type="button" tabIndex="-1" onClick={() => setMostrarContrasena(!mostrarContrasena)}>
                    {mostrarContrasena 
                      ? <img src="/images/ojo.svg" alt="Ocultar" /> 
                      : <img src="/images/ojo-cruzado.svg" alt="Mostrar" />}
                  </button>
                </div>
              </div>
              <div className="label-form">
                <label htmlFor="confirmar-contrasena">Confirmar contraseña:</label>
                <div className="contrasena-input">
                  <input
                    type={mostrarContrasena ? 'text' : 'password'}
                    placeholder="Confirmar contraseña"
                    value={confirmarContrasena}
                    onChange={(e) => setConfirmarContrasena(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, handleCrearContrasena)}

                  />
                  <button type="button" tabIndex="-1" onClick={() => setMostrarContrasena(!mostrarContrasena)}>
                    {mostrarContrasena 
                      ? <img src="/images/ojo.svg" alt="Ocultar" /> 
                      : <img src="/images/ojo-cruzado.svg" alt="Mostrar" />}
                  </button>
                </div>
              </div>
              <button className="boton-iniciar-sesion" onClick={handleCrearContrasena} disabled={loading}>
                {loading ? 'Iniciando sesión...' : 'Guardar y acceder'}
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
